import { fileUtils } from '../file';
import type { ExportData, ExportOptions } from './types';

const BOM = '\uFEFF';
const DEFAULT_DELIMITER = ',';

/**
 * Escapes special characters in a CSV cell
 * @param cell - Cell value to escape
 * @param delimiter - CSV delimiter
 * @returns Escaped cell value
 * @example
 * escapeCell('Hello, World', ',') // '"Hello, World"'
 * escapeCell('Quote"Test', ',') // '"Quote""Test"'
 */
function escapeCell(cell: string | number | boolean | null, delimiter: string): string {
  if (cell === null || cell === undefined) {
    return '';
  }

  const stringValue = String(cell);

  if (
    stringValue.includes(delimiter) ||
    stringValue.includes('"') ||
    stringValue.includes('\n') ||
    stringValue.includes('\r')
  ) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }

  return stringValue;
}

/**
 * Converts a row array to CSV format
 * @param row - Array of cell values
 * @param delimiter - CSV delimiter
 * @returns CSV formatted row string
 * @example
 * escapeRow(['Name', 'Age', 'City'], ',') // 'Name,Age,City'
 */
function escapeRow(row: (string | number | boolean | null)[], delimiter: string): string {
  return row.map((cell) => escapeCell(cell, delimiter)).join(delimiter);
}

/**
 * Converts data to CSV string format
 * @param data - Data with headers and rows
 * @param options - Export options
 * @returns CSV formatted string with BOM
 * @example
 * convertToCSV({
 *   headers: ['Name', 'Age'],
 *   rows: [['John', 25], ['Jane', 30]]
 * }) // '\uFEFFName,Age\nJohn,25\nJane,30'
 */
function convertToCSV(data: ExportData, options?: ExportOptions): string {
  const delimiter = options?.delimiter || DEFAULT_DELIMITER;
  const includeHeaders = options?.includeHeaders !== false;

  const rows: string[] = [];

  if (includeHeaders && data.headers.length > 0) {
    rows.push(escapeRow(data.headers, delimiter));
  }

  data.rows.forEach((row) => {
    rows.push(escapeRow(row, delimiter));
  });

  return BOM + rows.join('\n');
}

/**
 * Downloads CSV string as a file
 * @param csv - CSV string content
 * @param filename - Name of the downloaded file
 * @example
 * downloadCSV('Name,Age\nJohn,25', 'users.csv')
 */
function downloadCSV(csv: string, filename: string): void {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  fileUtils.downloadFile(blob, filename);
}

/**
 * Exports data to CSV file and triggers download
 * @param data - Data to export
 * @param options - Export configuration
 * @example
 * exportToCSV({
 *   headers: ['Product', 'Price', 'Stock'],
 *   rows: [['Laptop', 999, 15], ['Mouse', 29, 100]]
 * }, { filename: 'inventory.csv' })
 */
export function exportToCSV(data: ExportData, options?: ExportOptions): void {
  try {
    const csv = convertToCSV(data, options);
    downloadCSV(csv, options?.filename || 'export.csv');
  } catch (error) {
    console.error('Failed to export CSV:', error);
    throw new Error('Failed to export data to CSV');
  }
}

/**
 * Generates CSV string without downloading
 * @param data - Data to convert
 * @param options - Export configuration
 * @returns CSV formatted string
 * @example
 * const csv = generateCSV({
 *   headers: ['Name', 'Email'],
 *   rows: [['John', 'john@example.com']]
 * })
 */
export function generateCSV(data: ExportData, options?: ExportOptions): string {
  return convertToCSV(data, options);
}
